import React, { useEffect, useState } from 'react';
import { X, AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { Contact, OptInStatus } from '../../../types/models/whatsapp';

interface EditContactDialogProps {
  contact: Contact | null;
  onClose: () => void;
  onSave: (
    contactId: string,
    changes: {
      name?: string;
      tags?: string[];
      attributes?: Record<string, string | number | boolean>;
      optInStatus?: OptInStatus;
    }
  ) => Promise<void>;
}

type AttributeRow = { key: string; value: string };

const OPT_IN_OPTIONS: { value: OptInStatus; label: string; hint: string }[] = [
  { value: 'opted_in', label: 'Opted in', hint: 'Consent on record' },
  {
    value: 'unknown',
    label: 'Not recorded',
    hint: 'Sending still allowed'
  },
  { value: 'opted_out', label: 'Opted out', hint: 'Blocks all sending' }
];

/**
 * Edits the contact fields an agent owns.
 *
 * The phone number is deliberately read-only: it is the canonical key that
 * conversations, broadcast recipients and the webhook all resolve against, so
 * changing it would silently detach a contact's history. To reach a different
 * number, add a contact for it.
 */
const EditContactDialog: React.FC<EditContactDialogProps> = ({
  contact,
  onClose,
  onSave
}) => {
  const [name, setName] = useState('');
  const [tags, setTags] = useState('');
  const [optInStatus, setOptInStatus] = useState<OptInStatus>('unknown');
  const [attributes, setAttributes] = useState<AttributeRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!contact) return;
    setName(contact.name || '');
    setTags((contact.tags || []).join(', '));
    setOptInStatus(contact.optIn?.status || 'unknown');
    setAttributes(
      Object.entries(contact.attributes || {}).map(([key, value]) => ({
        key,
        value: String(value)
      }))
    );
    setError(null);
  }, [contact]);

  if (!contact) return null;

  const updateAttribute = (index: number, patch: Partial<AttributeRow>) => {
    setAttributes((rows) =>
      rows.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);

    try {
      const parsedAttributes: Record<string, string | number | boolean> = {};
      attributes.forEach(({ key, value }) => {
        const trimmed = key.trim();
        if (!trimmed) return;
        // Keep numbers numeric so template variables render "12" not "12.00",
        // but never coerce something like a postcode with a leading zero.
        const asNumber = Number(value);
        parsedAttributes[trimmed] =
          value.trim() !== '' &&
          !Number.isNaN(asNumber) &&
          String(asNumber) === value.trim()
            ? asNumber
            : value;
      });

      await onSave(contact.id, {
        name: name.trim(),
        tags: tags
          .split(',')
          .map((tag) => tag.trim().toLowerCase().replace(/\s+/g, '-'))
          .filter(Boolean),
        attributes: parsedAttributes,
        optInStatus
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Could not save this contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-sm flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Edit contact</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          <div>
            <span className="text-xs font-medium text-gray-700">
              WhatsApp number
            </span>
            <p className="mt-1 rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-600">
              {contact.phone}
            </p>
            <span className="mt-1 block text-[11px] text-gray-500">
              The number cannot be changed — it links this contact to their
              chat history.
            </span>
          </div>

          <label className="block">
            <span className="text-xs font-medium text-gray-700">Name</span>
            <input
              type="text"
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="mt-1 w-full rounded-lg border-gray-300 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500"
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium text-gray-700">
              Tags (comma separated)
            </span>
            <input
              type="text"
              value={tags}
              onChange={(event) => setTags(event.target.value)}
              placeholder="football, noida"
              className="mt-1 w-full rounded-lg border-gray-300 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500"
            />
          </label>

          <div>
            <span className="text-xs font-medium text-gray-700">Opt-in status</span>
            <div className="mt-1 space-y-1">
              {OPT_IN_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={`flex cursor-pointer items-center gap-2 rounded-lg border px-2.5 py-1.5 ${
                    optInStatus === option.value
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="optInStatus"
                    checked={optInStatus === option.value}
                    onChange={() => setOptInStatus(option.value)}
                    className="text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-xs font-medium text-gray-900">
                    {option.label}
                  </span>
                  <span className="ml-auto text-[10px] text-gray-500">
                    {option.hint}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-700">
                Custom fields
              </span>
              <button
                type="button"
                onClick={() =>
                  setAttributes((rows) => [...rows, { key: '', value: '' }])
                }
                className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-primary-600 hover:bg-primary-50"
              >
                <Plus className="h-3 w-3" />
                Add
              </button>
            </div>

            {attributes.length === 0 ? (
              <p className="mt-1 text-[11px] text-gray-500">
                None yet. Custom fields fill template variables in campaigns.
              </p>
            ) : (
              <div className="mt-1 space-y-1.5">
                {attributes.map((row, index) => (
                  <div key={index} className="flex items-center gap-1.5">
                    <input
                      type="text"
                      value={row.key}
                      onChange={(event) =>
                        updateAttribute(index, { key: event.target.value })
                      }
                      placeholder="Field"
                      className="min-w-0 flex-1 rounded-lg border-gray-300 text-xs text-gray-900 focus:border-primary-500 focus:ring-primary-500"
                    />
                    <input
                      type="text"
                      value={row.value}
                      onChange={(event) =>
                        updateAttribute(index, { value: event.target.value })
                      }
                      placeholder="Value"
                      className="min-w-0 flex-1 rounded-lg border-gray-300 text-xs text-gray-900 focus:border-primary-500 focus:ring-primary-500"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setAttributes((rows) =>
                          rows.filter((_, i) => i !== index)
                        )
                      }
                      className="shrink-0 rounded p-1 text-gray-400 hover:text-danger-600"
                      aria-label="Remove field"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {optInStatus === 'opted_out' && (
            <p className="flex items-start gap-1.5 rounded bg-warning-50 p-2 text-[11px] text-black">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              Opting a contact out stops all outbound messages, including
              campaigns. Only set this on the contact's own request.
            </p>
          )}

          {error && <p className="text-xs text-danger-600">{error}</p>}
        </div>

        <div className="flex gap-2 border-t border-gray-200 p-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditContactDialog;
